"use client"

import { useState } from "react"
import { ThumbsUp, ThumbsDown } from "lucide-react"
import { Message } from "./types"
import { FeedbackDialog } from "./FeedbackDialog"
import { getToolRenderer } from "@/hooks/useToolRenderer"
import { MarkdownRenderer } from "./MarkdownRenderer"
import { QuickActions, extractQuickActions } from "./QuickActions"

interface ChatMessageProps {
  message: Message
  sessionId: string
  onFeedbackSubmit: (feedbackType: "positive" | "negative", comment: string) => Promise<void>
  onQuickAction?: (action: string) => void
  isLastAssistant?: boolean
  isLoading?: boolean
}

export function ChatMessage({
  message,
  sessionId: _sessionId,
  onFeedbackSubmit,
  onQuickAction,
  isLastAssistant,
  isLoading,
}: ChatMessageProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedFeedbackType, setSelectedFeedbackType] = useState<"positive" | "negative">("positive")
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)

  const formatTime = (timestamp: string) =>
    new Date(timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })

  const handleFeedbackClick = (type: "positive" | "negative") => {
    setSelectedFeedbackType(type)
    setIsDialogOpen(true)
  }

  const handleFeedbackSubmit = async (comment: string) => {
    await onFeedbackSubmit(selectedFeedbackType, comment)
    setFeedbackSubmitted(true)
  }

  const renderAssistantContent = () => {
    if (message.segments && message.segments.length > 0) {
      return message.segments.map((seg, i) => {
        if (seg.type === "text") {
          return <MarkdownRenderer key={i} content={seg.content} />
        }
        const render = getToolRenderer(seg.toolCall.name)
        if (!render) return null
        return (
          <div key={seg.toolCall.toolUseId} className="my-0.5">
            {render({
              name: seg.toolCall.name,
              args: seg.toolCall.input,
              status: seg.toolCall.status,
              result: seg.toolCall.result,
            })}
          </div>
        )
      })
    }
    return <MarkdownRenderer content={message.content} />
  }

  const quickActions = message.role === "assistant" && isLastAssistant && !isLoading && message.content
    ? extractQuickActions(message.content)
    : []

  return (
    <div className={`flex gap-3 ${message.role === "user" ? "flex-row-reverse" : "flex-row"}`}>
      {/* Avatar */}
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 mt-0.5 ${
        message.role === "user" ? "bg-emerald-600" : "bg-emerald-500"
      }`}>
        {message.role === "user" ? "Y" : "T"}
      </div>

      {/* Message content */}
      <div className={`flex flex-col ${message.role === "user" ? "items-end" : "items-start"} max-w-[80%]`}>
        <div
          className={`break-words ${
            message.role === "user"
              ? "px-4 py-3 rounded-2xl bg-emerald-600 text-white rounded-br-sm whitespace-pre-wrap"
              : "text-gray-800"
          }`}
        >
          {message.role === "assistant" ? renderAssistantContent() : message.content}
        </div>

      {/* Quick Actions */}
      {quickActions.length > 0 && onQuickAction && (
        <QuickActions actions={quickActions} onAction={onQuickAction} disabled={isLoading} />
      )}

      {/* Timestamp + Feedback */}
      <div className="flex items-center gap-2 mt-1 px-1">
        <div className="text-xs text-gray-400">{formatTime(message.timestamp)}</div>
        {message.role === "assistant" && message.content && (
          <div className="flex items-center gap-1 ml-2">
            <button
              onClick={() => handleFeedbackClick("positive")}
              disabled={feedbackSubmitted}
              className="p-1 text-gray-300 hover:text-emerald-500 hover:bg-emerald-50 rounded transition-colors disabled:opacity-50"
              title="Good response"
            >
              <ThumbsUp size={13} />
            </button>
            <button
              onClick={() => handleFeedbackClick("negative")}
              disabled={feedbackSubmitted}
              className="p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
              title="Bad response"
            >
              <ThumbsDown size={13} />
            </button>
            {feedbackSubmitted && <span className="text-xs text-gray-400 ml-1">Thanks!</span>}
          </div>
        )}
      </div>

      </div>

      <FeedbackDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={handleFeedbackSubmit}
        feedbackType={selectedFeedbackType}
      />
    </div>
  )
}
